/**
 * @file THE MIXER — the pure model half of the sound system. It decides WHICH
 * sounds get a voice, HOW LOUD they are, WHERE they sit in the stereo field and
 * WHICH clip of a variant set plays. It never makes a sound: there is no
 * `AudioContext` here, no node, no buffer and no fetch.
 *
 * The split is `systems/Speech.js` (model, node-testable) against
 * `render/SpeechBubbles.js` (pixels), taken again: `audio/AudioBackend.js` owns
 * the Web Audio graph, `audio/Sfx.js` joins the two, and everything in this file
 * is provable in node with duck types, no GPU and no browser.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE IMPORT, AND IT IS `../math/rng.js`
 * ─────────────────────────────────────────────────────────────────────────────
 * No three.js, no DOM, no timers and no clock of its own — **every call that
 * cares about time is TOLD the time** (`request(slug, opts, now)`,
 * `touch(voice, now)`, `update(now)`). A model that read `performance.now()`
 * would be a model no suite could step, and `minGap` would be untestable.
 *
 * Everything injected is null-safe: `createMixer({})` constructs, accepts every
 * call and does nothing. That is the `Wallet.js` / `Weapons.js` /
 * `VehicleDamage.js` discipline, and it is what lets hero mode and every dev
 * scene build a silent audio stack rather than a branch around one.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠⚠ RULE 4 — AND WHY THIS FILE'S DICE ARE NOT SIM DICE
 * ─────────────────────────────────────────────────────────────────────────────
 * `Math.random()` is banned under `src/`, so the two knobs that need variety —
 * `pick: 'random'` and `rateVar` — draw from ONE private `mulberry32` this
 * module owns, seeded off the level seed with its own salt.
 *
 * ⚠ **THAT STREAM IS DELIBERATELY NOT A SIM STREAM.** Nothing in the simulation
 * reads it, nothing in the simulation can advance it, and no generation stage
 * shares it. A gunshot's variant depends on how many gunshots have been fired,
 * which depends on how the player played — that is exactly the dependency rule 4
 * exists to keep OUT of the level's stage streams, and it is fine here because
 * the only consequence is which of three cracks you hear. Concretely: the sound
 * table lives in `src/game/`, the engine never imports it, and with no table
 * injected this whole file allocates nothing and draws nothing — which is what
 * keeps hero mode's golden checksum **1878938342** safe BY CONSTRUCTION rather
 * than by a test somebody has to remember to re-run.
 *
 * ⚠ **`pick: 'sticky'` AND `rateKeyed` DRAW ZERO DICE.** They are a pure hash of
 * `slug + ':' + key`, so a car keeps its engine note across a save, a reload,
 * a level rebuild and a different order of play. A die here would make a car's
 * identity depend on when it happened to be spawned.
 *
 * ⚠ **A REJECTED REQUEST DRAWS NOTHING.** Every one of the five rejections
 * below happens before the first die: the variant and the rate are resolved
 * only once the voice is certain to be allocated. Otherwise the stream would
 * depend on how many sounds were culled, which is to say on where the camera
 * was pointed.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ THE REJECTION ORDER IS THE PERFORMANCE STORY, AND IT IS FIXED
 * ─────────────────────────────────────────────────────────────────────────────
 * `request()` runs, in this order and no other:
 *
 *   a. **unknown slug** → null (`dropped`)
 *   b. **beyond `maxDist`** → null (`culled`)
 *   c. **inside `minGap`** → null (`dropped`)
 *   d. **at the slug's own `maxVoices`** → steal this slug's OLDEST, else null
 *   d2. **at `tune.maxLoops`, and this is a loop** → steal a LOOP strictly below,
 *       else null (`dropped`)
 *   e. **pool full** → steal the lowest `(priority, then quietest, then oldest)`
 *       STRICTLY below this one, else null (`dropped`)
 *   f. allocate; resolve variant, rate, gain and pan; return them
 *
 * ⚠ **(b) IS BEFORE ANY ALLOCATION AND ANY BOOK-KEEPING, AND THAT IS THE WHOLE
 * POINT.** `src/game/sfx.js` records it: in a city holding ~64 live actors and a
 * traffic fleet, most requests in any given second are rejected here, and the
 * rejection must cost one squared-distance and one compare — no node, no
 * buffer, no voice, and *no `lastStart` write*, so a gunshot culled at 200 m
 * cannot eat the `minGap` of the one fired at your feet a frame later. The
 * distance is compared SQUARED for the same reason: a cull may not pay for a
 * square root.
 *
 * ⚠ **THE STEAL IS STRICTLY BELOW, AND A TIE NEVER STEALS.** A `<=` here reads
 * as harmless and is not: five gunshots inside one 0.28 s interval all carry
 * `priority` 8, so with a tie-steal each new crack cuts the previous one 12 ms
 * in and the burst collapses into a single click. `docs/actors.md`'s "eight
 * pistol cracks is a burst of white noise" is about THINNING the burst on
 * purpose (`maxVoices` 5); this is about not destroying the five that survive.
 *
 * ⚠ **A LOOP IS NOT A ONE-SHOT WITH A FLAG.** A loop holds a real node for
 * minutes, so it is budgeted against `tune.maxLoops` as WELL as
 * `tune.maxVoices`: a street of idling cars must not be able to take the pool
 * and leave the gunfire nothing to steal. A loop can therefore be refused with
 * two thirds of the pool free, which is correct and looks like a bug until you
 * read this paragraph.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ THE PAN SIGN — A MIRROR IS CORRECT IN A SCREENSHOT AND UNUSABLE IN MOTION
 * ─────────────────────────────────────────────────────────────────────────────
 * `docs/ui.md` records that failure mode for the mission arrow's bearing, and a
 * mirrored pan is the same bug in the ears: everything sounds plausible until
 * you turn the camera, and then the world swaps sides. So the camera-right
 * vector here is DERIVED from the engine's own convention rather than guessed.
 *
 * `core/Engine.js#updateCamera` puts the eye at `target + (sin yaw, ., cos yaw)
 * * d` looking at the target, so the ground forward is `f = (-sin yaw, -cos
 * yaw)`; camera right is `cross(forward, +Y)` = `(-f.z, f.x)` — the same
 * right-hand rule `docs/vehicles.md` records for a lane heading — which is
 * **`(cos yaw, -sin yaw)`**. `tests/minimap.test.mjs` (a) restates the same two
 * lines from the ENGINE and probes them; `tests/audio.test.mjs` (f) probes THIS
 * file at six yaws with a mirrored negative control, because an up/down test
 * cannot see a mirror and neither can an ear that has not turned round yet.
 *
 * @module engine/audio/mixer
 */

import { hashSeed, mulberry32 } from '../math/rng.js';

/**
 * A voice slot's state. `FADING` is a loop on its way out: it still holds a
 * backend node, so it still counts against both budgets, but it is already
 * finished as far as the player is concerned — which is why it is the FIRST
 * thing stealing takes, whatever its priority.
 */
export const VOICE = Object.freeze({ FREE: 0, LIVE: 1, FADING: 2 });

/**
 * ⚠⚠ **THESE ARE FALLBACKS, NOT A COPY OF `SFX_TUNE`, AND THEY MUST NEVER BE
 * MADE ONE.** `src/game/sfx.js` is content and the engine may not import it, so
 * a mixer built with no `tune` needs *something* — but making these equal the
 * shipped numbers would create the `links.js`/`countryroads.js` mirroring trap
 * for values nobody would think to keep in step. They are deliberately generic:
 * a small pool, no cull fade (the degenerate case the real `cullFade` exists to
 * fix) and unity pan width (the identity). A mixer that reaches them is a mixer
 * with no sound table, and a mixer with no sound table allocates nothing at all.
 */
const FALLBACK = Object.freeze({ maxVoices: 16, maxLoops: 4, cullFade: 0, panWidth: 1 });

/**
 * Seconds a one-shot occupies its slot when NOBODY told the mixer how long the
 * clip is — neither `assets` at construction nor `opts.dur` at the call. Long
 * enough to cover the short end of the pack, short enough that an unknown
 * duration cannot wedge a slot. `Sfx.js` should always pass the DECODED
 * duration, which is the only one that counts (`sfxAssets.js` durations include
 * MP3 encoder padding).
 */
const FALLBACK_DUR = 1.5;

/**
 * A rate of zero stalls an `AudioBufferSourceNode` for ever and a negative one
 * is not a thing. The clamp is a floor on the product of every rate term, not a
 * limit on any one of them.
 */
const MIN_RATE = 0.05;

/** @param {any} n @returns {boolean} A real, usable number. `Wallet.js#usable`. */
const usable = (n) => typeof n === 'number' && Number.isFinite(n);

/** @param {any} v @param {number} d @returns {number} `v` if usable, else `d`. */
const num = (v, d) => (usable(v) ? v : d);

/** @param {number} v @param {number} lo @param {number} hi @returns {number} */
const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

/**
 * @param {any} v @param {number} d
 * @returns {number} `v` as a whole count >= 0, or `d`. A `0` is legal and means
 *   zero — that is why this is not `v || d`.
 */
const count = (v, d) => (usable(v) && v >= 0 ? Math.floor(v) : d);

/**
 * @param {any} v
 * @returns {number} A per-slug cap. Absent means UNCAPPED, not zero — most rows
 *   in the table set `maxVoices` and the ones that do not are meant to be free.
 */
const capOf = (v) => (usable(v) && v >= 0 ? Math.floor(v) : Infinity);

/** @param {string|number|null|undefined} s @returns {number} Hash in `[0,1)`. */
const hash01 = (s) => hashSeed(String(s)) / 4294967296;

// ─────────────────────────────────────────────────────────────────────────────
// The two pure laws. Exported so the suite and the review scene call THESE and
// not a second copy — a mixer whose maths lives inline is a mixer whose maths
// gets restated in a dev scene and drifts.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * THE DISTANCE LAW. Full gain inside `refDist`, `refDist / d` beyond it (the
 * inverse-distance law every game uses), multiplied by a linear ramp to zero
 * across the last `cullFade` fraction of `maxDist`.
 *
 * ⚠ **CONTINUOUS AT BOTH ENDS, AND BOTH ENDS COST SOMETHING TO GET WRONG.** At
 * `refDist` the two branches meet at exactly 1 (`ref / ref`), so there is no
 * step where a sound crosses its own reference radius — a step there is a click
 * on every car that drives past you. At `maxDist` it reaches EXACTLY 0 rather
 * than some small survivor, because `request` culls at that same radius and a
 * loop crossing the boundary at gain 0.03 pops out mid-cycle.
 *
 * ⚠ `cullFade` 0 is the DEGENERATE case and it is legal: a hard cut at
 * `maxDist`. That is precisely what `SFX_TUNE.cullFade` exists to stop, and it
 * is the fallback here so that a mixer with no tune cannot silently claim a
 * fade nobody configured.
 *
 * Monotonically non-increasing in `d` by construction: a non-increasing
 * `ref / d` times a non-increasing non-negative ramp.
 *
 * @param {number} d Distance, metres. THREE-DIMENSIONAL — see `request`.
 * @param {number} [refDist=1] Metres inside which the sound is at full gain.
 * @param {number} [maxDist=Infinity] Metres past which it is silent.
 * @param {number} [cullFade=0] Fraction of `maxDist` spent ramping to zero.
 * @returns {number} `0..1`. A non-finite distance is 0, never 1 — an unknown
 *   position must not arrive at full volume.
 */
export function attenuate(d, refDist = 1, maxDist = Infinity, cullFade = 0) {
  if (!usable(d)) return 0;
  const dist = d < 0 ? 0 : d;
  const max = usable(maxDist) ? maxDist : Infinity;
  if (dist >= max) return 0;

  const ref = Math.max(1e-6, num(refDist, 1));
  let g = dist <= ref ? 1 : ref / dist;

  if (max < Infinity && max > 0) {
    const fade = clamp(num(cullFade, 0), 0, 1) * max;
    // The ramp is 1 at `max - fade` and 0 at `max`, so it joins the
    // inverse-distance curve without a step and lands on zero exactly.
    if (fade > 0 && dist > max - fade) g *= (max - dist) / fade;
  }
  return clamp(g, 0, 1);
}

/**
 * THE PAN LAW. The source offset projected onto the camera's RIGHT vector,
 * normalised by its own length so the answer is a direction and not a distance,
 * times `width`.
 *
 * ⚠⚠ **THE SIGN IS THE WHOLE RISK.** Camera right is `(cos yaw, -sin yaw)` —
 * DERIVED, in the file header, from `Engine#updateCamera`'s eye placement and
 * the same `cross(forward, +Y)` right-hand rule the traffic code uses, and
 * probed at six yaws in `tests/audio.test.mjs` (f) against a mirrored control.
 * A mirrored pan is `docs/ui.md`'s recorded failure verbatim: correct in a
 * screenshot, unusable in motion.
 *
 * ⚠ **Y IS NOT IN IT.** A sound directly overhead or 100 m below in a shop
 * interior pans CENTRE, which is the only honest answer a stereo field can
 * give. Height belongs to {@link attenuate}, which does take it.
 *
 * ⚠ **THE ZERO OFFSET IS GUARDED, NOT ASSUMED.** `mapRender.js#screenBearing`
 * carries the same guard for the same family of reason — there it is that
 * `atan2(0, -0)` is `PI` and tells a viewer standing on their target to turn
 * round; here it is that a normalisation by zero is `NaN`, and a `NaN` pan
 * silences a `StereoPannerNode` for good. A listener standing exactly on a
 * source pans centre.
 *
 * @param {number} dx World X, SOURCE minus listener.
 * @param {number} dz World Z, SOURCE minus listener.
 * @param {number} yaw The CAMERA yaw, radians. Free-running is fine.
 * @param {number} [width=1] `SFX_TUNE.panWidth`. 1 is a hard pan at 90 degrees
 *   off the camera axis.
 * @returns {number} `-1..1`. Positive is RIGHT.
 */
export function panFor(dx, dz, yaw, width = 1) {
  if (!usable(dx) || !usable(dz) || (dx === 0 && dz === 0)) return 0;
  const d = Math.hypot(dx, dz);
  if (!(d > 0)) return 0;
  const y = num(yaw, 0);
  // camera right = (cos yaw, -sin yaw). See the header for the derivation.
  const p = ((dx * Math.cos(y) - dz * Math.sin(y)) / d) * num(width, 1);
  return clamp(p, -1, 1);
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * @typedef {object} Voice
 * @property {number} state One of {@link VOICE}.
 * @property {string} slug
 * @property {string} key Entity key, `''` when the caller gave none.
 * @property {number} prio Resolved priority — the def's, or the caller's override.
 * @property {number} gain Resolved gain AT START. Stealing reads it, so a
 *   distant sound is taken before a near one of the same priority.
 * @property {boolean} loop
 * @property {number} startedAt
 * @property {number} touchedAt Last {@link Mixer.touch}; a loop's keep-alive.
 * @property {number} endsAt `Infinity` for a loop.
 */

/**
 * The voice book. It never makes a sound.
 *
 * @param {Object} [o]
 * @param {Record<string, any>|null} [o.mix] `createSoundTable().mix` — the
 *   per-slug tuning. **Absent means every slug is unknown**, so every
 *   `request()` returns null at step (a) and nothing is ever allocated. That is
 *   the gate, and it is why a mixer with no table is silent rather than special.
 * @param {Object|null} [o.tune] `createSoundTable().tune`. See {@link FALLBACK}.
 * @param {string|number} [o.seed] The level seed. One private stream is derived
 *   from it; see the header for why that stream is not a sim stream.
 * @param {Record<string, {kind?: string, files?: string[], dur?: number[]}>|null} [o.assets]
 *   `createSoundTable().assets`. OPTIONAL and null-safe. It answers two
 *   questions the mix table cannot: **how many variants a slug has** and
 *   **whether it loops**. ⚠ Without it every slug resolves to variant 0 — the
 *   only variant a mixer that has not been told what exists can prove is
 *   there — and `loop` comes from `opts.loop` alone. `Sfx.js` passes it.
 * @returns {Object} The mixer.
 */
export function createMixer(o = {}) {
  const { mix = null, tune = null, seed = 0, assets = null } = o ?? {};

  // ── the machine's limits, resolved once ────────────────────────────────────
  const maxVoices = count(tune?.maxVoices, FALLBACK.maxVoices);
  /** ⚠ Clamped to the pool: a loop budget above the pool size is not a budget. */
  const maxLoops = Math.min(maxVoices, count(tune?.maxLoops, FALLBACK.maxLoops));
  const cullFade = clamp(num(tune?.cullFade, FALLBACK.cullFade), 0, 1);
  const panWidth = clamp(num(tune?.panWidth, FALLBACK.panWidth), 0, 1);

  /**
   * ⚠ THE ONE PRIVATE STREAM. Written as an explicit `mulberry32(hashSeed(seed)
   * + SALT)` rather than through `streamRng` ON PURPOSE: `streamRng` is the
   * per-GENERATION-STAGE discipline, and this is emphatically not a generation
   * stage. See the header — nothing in the sim reads this, and nothing in the
   * sim can advance it.
   */
  const rng = mulberry32((hashSeed(seed) + hashSeed('audio:mixer')) >>> 0);

  /** @type {Voice[]} Allocated once. The index IS the voice id. */
  const pool = [];
  for (let i = 0; i < maxVoices; i++) {
    pool.push({ state: VOICE.FREE, slug: '', key: '', prio: 0, gain: 0, loop: false, startedAt: 0, touchedAt: 0, endsAt: 0 });
  }

  /** @type {Map<string, number>} Last START time per slug — the `minGap` book. */
  const lastStart = new Map();

  // ── the listener ───────────────────────────────────────────────────────────
  let lx = 0;
  let ly = 0;
  let lz = 0;
  let lyaw = 0;

  /** The last time anybody told us. A model with no clock still needs a `now`. */
  let lastNow = 0;
  let disposed = false;

  // ── counters ───────────────────────────────────────────────────────────────
  let stolen = 0;
  let culled = 0;
  let dropped = 0;
  let peak = 0;

  // ── the book ───────────────────────────────────────────────────────────────

  /**
   * ⚠ COUNTED BY SCANNING, NEVER BY AN INCREMENTAL COUNTER. The pool is at most
   * `tune.maxVoices` (24 shipped) and `request` runs a few dozen times a second,
   * so the scan is free — and a pair of counters that can drift out of step with
   * the array they describe is the `(handle, serial)` family of bug with a
   * quieter failure: an audio graph that thinks it is full.
   * @returns {{live: number, loops: number}}
   */
  const _count = () => {
    let live = 0;
    let loops = 0;
    for (let i = 0; i < pool.length; i++) {
      if (pool[i].state === VOICE.FREE) continue;
      live++;
      if (pool[i].loop) loops++;
    }
    return { live, loops };
  };

  /** @param {string} slug @returns {number} Live voices carrying this slug. */
  const _countSlug = (slug) => {
    let n = 0;
    for (let i = 0; i < pool.length; i++) if (pool[i].state !== VOICE.FREE && pool[i].slug === slug) n++;
    return n;
  };

  /** @param {number} i */
  const _free = (i) => {
    const v = pool[i];
    v.state = VOICE.FREE;
    v.slug = '';
    v.key = '';
    v.prio = 0;
    v.gain = 0;
    v.loop = false;
    v.endsAt = 0;
  };

  /** @returns {number} The first FREE slot, or -1. */
  const _firstFree = () => {
    for (let i = 0; i < pool.length; i++) if (pool[i].state === VOICE.FREE) return i;
    return -1;
  };

  /**
   * @param {string} slug
   * @returns {number} This slug's OLDEST live voice, or -1. Step (d)'s victim:
   *   a slug at its own cap always cannibalises itself rather than reaching into
   *   the pool, so `gunshot`'s deliberate thinning can never cost a siren.
   */
  const _oldestOf = (slug) => {
    let best = -1;
    for (let i = 0; i < pool.length; i++) {
      const v = pool[i];
      if (v.state === VOICE.FREE || v.slug !== slug) continue;
      if (best < 0 || v.startedAt < pool[best].startedAt) best = i;
    }
    return best;
  };

  /**
   * THE STEAL RANK: `(priority ASC, gain ASC, age DESC)` — the least important,
   * then the quietest, then the one that has been running longest. A `FADING`
   * voice sorts ahead of all of them: it is already leaving.
   * @param {Voice} a @param {Voice} b @returns {boolean} `a` is taken before `b`.
   */
  const _ranksBefore = (a, b) => {
    const ad = a.state === VOICE.FADING ? 0 : 1;
    const bd = b.state === VOICE.FADING ? 0 : 1;
    if (ad !== bd) return ad < bd;
    if (a.prio !== b.prio) return a.prio < b.prio;
    if (a.gain !== b.gain) return a.gain < b.gain;
    return a.startedAt < b.startedAt;
  };

  /**
   * @param {number} prio The INCOMING sound's priority.
   * @param {boolean} loopOnly Only a loop may be taken (the `maxLoops` budget:
   *   freeing a one-shot would not make room under a ceiling on loops).
   * @returns {number} The victim's index, or -1 when nothing may be taken.
   *
   * ⚠ **STRICTLY BELOW.** `v.prio < prio`, never `<=`. See the header: a tie
   * turns a five-round burst into one click. The only exception is a `FADING`
   * voice, which is stealable at any priority because it is already on its way
   * out and holding a node nobody is listening to.
   */
  const _pickSteal = (prio, loopOnly) => {
    let best = -1;
    for (let i = 0; i < pool.length; i++) {
      const v = pool[i];
      if (v.state === VOICE.FREE) continue;
      if (loopOnly && !v.loop) continue;
      if (v.state !== VOICE.FADING && !(v.prio < prio)) continue;
      if (best < 0 || _ranksBefore(v, pool[best])) best = i;
    }
    return best;
  };

  // ── resolving a def ────────────────────────────────────────────────────────

  /**
   * @param {any} def @param {string} slug
   * @returns {{kind?: string, files?: string[], dur?: number[]}|null} The asset
   *   row. ⚠ An ALIAS resolves through `def.src`, which `createSoundTable()`
   *   already filled in — the mixer must never learn that `explosion` borrows
   *   `car_crash`'s buffers. That is a fact about which clips shipped, and it
   *   changes the day a real explosion arrives.
   */
  const _assetOf = (def, slug) => assets?.[def?.src ?? slug] ?? null;

  /** @param {any} def @param {string} slug @returns {number} Variant count, >= 1. */
  const _variantCount = (def, slug) => {
    const n = _assetOf(def, slug)?.files?.length;
    return usable(n) && n >= 1 ? Math.floor(n) : 1;
  };

  /**
   * Seconds this voice will hold its slot. `opts.dur` wins — `Sfx.js` holds the
   * DECODED buffer, and the decoder's answer is the only one that counts.
   *
   * ⚠ DIVIDED BY THE RATE. `explosion` is `car_crash_1` at 0.62x, so a 0.96 s
   * clip occupies its slot for 1.55 s; a slot freed at the clip's nominal length
   * would be handed out while the source was still sounding.
   *
   * @param {any} def @param {string} slug @param {number} variant
   * @param {any} opts @param {number} rate @returns {number}
   */
  const _durOf = (def, slug, variant, opts, rate) => {
    const given = opts?.dur;
    const tabled = _assetOf(def, slug)?.dur?.[variant];
    const base = usable(given) && given > 0 ? given : usable(tabled) && tabled > 0 ? tabled : FALLBACK_DUR;
    return base / Math.max(MIN_RATE, rate);
  };

  /**
   * @param {any} def @param {string} slug @param {any} opts
   * @returns {number} The playback rate. Three independent terms, multiplied:
   *   the def's own `rate` (the `explosion` pitch-down), a KEYED offset (a pure
   *   hash — the sticky pitch that makes one engine buffer sound like a fleet)
   *   and a per-play JITTER (the only die).
   */
  const _rateFor = (def, slug, opts) => {
    let r = num(def.rate, 1) * num(opts?.rate, 1);

    const keyed = num(def.rateKeyed, 0);
    if (keyed !== 0) {
      // ⚠ ZERO DICE. Hashed from the entity key, so a car holds its note for
      // ever and across a rebuild. `key` absent hashes the slug alone, which is
      // stable but shared — a caller that wants distinct pitches must pass one.
      r *= 1 + keyed * (hash01(`${slug}:${opts?.key ?? ''}`) * 2 - 1);
    }

    const jitter = num(def.rateVar, 0);
    // ⚠ THE SECOND (and last) DIE, and it is drawn only on a real allocation.
    if (jitter !== 0) r *= 1 + jitter * (rng() * 2 - 1);

    return Math.max(MIN_RATE, r);
  };

  const api = {
    name: 'mixer',

    // ── the listener ─────────────────────────────────────────────────────────

    /**
     * ⚠ **CALL THIS BEFORE ANY `request` IN A FRAME.** Everything positional is
     * evaluated against the listener as it stood when the sound was asked for,
     * so a stale listener pans a whole frame's worth of sound at last frame's
     * camera — which on a fast turn is audible.
     *
     * ⚠ The listener is the PLAYER, not `interiors.focus` — `src/game/sfx.js`
     * and `docs/dialogue.md` both record why: a shop is a real room 100 m under
     * the island, and anchoring on the door ring pipes the whole street into it.
     * That decision belongs to the caller; this method just stores four numbers.
     *
     * @param {number} x @param {number} y @param {number} z
     * @param {number} yaw CAMERA yaw, radians.
     */
    setListener(x, y, z, yaw) {
      if (disposed) return;
      lx = num(x, lx);
      ly = num(y, ly);
      lz = num(z, lz);
      lyaw = num(yaw, lyaw);
    },

    // ── THE core call ────────────────────────────────────────────────────────

    /**
     * Ask for a voice. **Never throws, never awaits, returns `null` when the
     * sound is not going to play.** The rejection order is in the file header
     * and is not negotiable.
     *
     * A sound is POSITIONAL when `opts.x` and `opts.z` are both real numbers
     * (`y` defaults to the listener's plane). Anything else is 2D: it skips the
     * distance cull entirely and pans centre — which is what the `ui` bus and
     * the rain bed want, and `src/game/sfx.js` records that a panned rain loop
     * swings the whole weather round the player's head.
     *
     * @param {string} slug
     * @param {Object|null} [opts]
     * @param {number} [opts.x] @param {number} [opts.y] @param {number} [opts.z]
     * @param {string} [opts.key] Entity key — `pick:'sticky'` and `rateKeyed`.
     * @param {number} [opts.gain] Extra multiplier on top of the slug's.
     * @param {number} [opts.rate] Extra multiplier on top of the slug's.
     * @param {number} [opts.priority] Override. Rarely used.
     * @param {boolean} [opts.loop] Whether this voice loops. Falls back to
     *   `assets[src].kind === 'loop'` when the mixer was given the assets.
     * @param {number} [opts.dur] The DECODED clip length, seconds. See `_durOf`.
     * @param {number} [now] Seconds, absolute. This module owns no clock.
     * @returns {{voice: number, variant: number, gain: number, pan: number,
     *   rate: number, loop: boolean}|null}
     */
    request(slug, opts = null, now = undefined) {
      if (disposed) return null;
      const t = usable(now) ? now : lastNow;
      lastNow = t;

      // ── a. is the slug known? ──────────────────────────────────────────────
      const def = mix?.[slug];
      if (!def) {
        dropped++;
        return null;
      }

      // ── b. DISTANCE, BEFORE ANY BOOK-KEEPING. See the header. ──────────────
      const ox = opts?.x;
      const oz = opts?.z;
      const positional = usable(ox) && usable(oz);
      let gain = num(def.gain, 1) * num(opts?.gain, 1);
      let pan = 0;

      if (positional) {
        const dx = ox - lx;
        const dy = num(opts?.y, ly) - ly;
        const dz = oz - lz;
        // ⚠ THE DISTANCE IS 3D, and that is the `docs/dialogue.md` rule: a shop
        // is a room 100 m down, and an XZ test pipes the street through its
        // ceiling. The compare is SQUARED so a cull never pays for a sqrt.
        const d2 = dx * dx + dy * dy + dz * dz;
        const max = num(def.maxDist, Infinity);
        if (max < Infinity && d2 >= max * max) {
          culled++;
          return null;
        }
        gain *= attenuate(Math.sqrt(d2), num(def.refDist, 1), max, cullFade);
        pan = panFor(dx, dz, lyaw, panWidth);
      }

      // ── c. minGap — seconds between two STARTS of this slug, whoever asked ─
      const gap = num(def.minGap, 0);
      if (gap > 0) {
        const last = lastStart.get(slug);
        // ⚠ A `now` that went BACKWARDS is allowed through rather than blocking
        // for ever: a rewound clock is a test or a context reset, not a burst.
        if (last !== undefined && t >= last && t - last < gap) {
          dropped++;
          return null;
        }
      }

      const prio = num(opts?.priority, num(def.priority, 0));
      const loop = typeof opts?.loop === 'boolean' ? opts.loop : _assetOf(def, slug)?.kind === 'loop';

      // ── d. the slug's own cap — it cannibalises ITSELF, never the pool ─────
      const cap = capOf(def.maxVoices);
      if (_countSlug(slug) >= cap) {
        const victim = _oldestOf(slug);
        // Only reachable at `maxVoices: 0`, which is a legal way to mute a row.
        if (victim < 0) {
          dropped++;
          return null;
        }
        _free(victim);
        stolen++;
      }

      let { live, loops } = _count();

      // ── d2. THE LOOP BUDGET — separate, and it can refuse with a free pool ─
      if (loop && loops >= maxLoops) {
        const victim = _pickSteal(prio, true);
        if (victim < 0) {
          dropped++;
          return null;
        }
        _free(victim);
        stolen++;
        live--;
        loops--;
      }

      // ── e. the pool ────────────────────────────────────────────────────────
      if (live >= maxVoices) {
        const victim = _pickSteal(prio, false);
        if (victim < 0) {
          dropped++;
          return null;
        }
        _free(victim);
        stolen++;
        live--;
      }

      // ── f. allocate ────────────────────────────────────────────────────────
      const i = _firstFree();
      if (i < 0) {
        // `maxVoices: 0`, i.e. a mixer configured to hold no voices at all.
        dropped++;
        return null;
      }

      // ⚠ THE DICE LIVE HERE AND NOWHERE ABOVE. Every rejection has already
      // happened, so the stream cannot depend on how many sounds were culled.
      // The ORDER is fixed — variant, then rate — for the reason
      // `docs/interiors.md` gives for its 99-die superset: a stream is an order.
      const variant = api.variantFor(slug, opts?.key);
      const rate = _rateFor(def, slug, opts);

      const v = pool[i];
      v.state = VOICE.LIVE;
      v.slug = slug;
      v.key = typeof opts?.key === 'string' ? opts.key : '';
      v.prio = prio;
      v.gain = gain;
      v.loop = loop;
      v.startedAt = t;
      v.touchedAt = t;
      v.endsAt = loop ? Infinity : t + _durOf(def, slug, variant, opts, rate);

      lastStart.set(slug, t);
      if (live + 1 > peak) peak = live + 1;

      return { voice: i, variant, gain, pan, rate, loop };
    },

    // ── the voice book ───────────────────────────────────────────────────────

    /**
     * Hand a slot back. Idempotent, and safe for a stale id — the backend calls
     * this from an `onended` that can fire after the mixer has already reaped
     * the voice and handed the slot to somebody else, so it is deliberately NOT
     * an error to release a slot that is already free.
     *
     * ⚠ It does NOT check that the caller still owns the slot: a backend that
     * releases somebody else's voice is a backend bug, and a mixer that carried
     * a generation counter to catch it would be inventing the `(handle, serial)`
     * machinery for a pool of 24 that one module owns end to end.
     *
     * @param {number} voice
     */
    release(voice) {
      if (disposed || !usable(voice)) return;
      const i = Math.floor(voice);
      if (i < 0 || i >= pool.length) return;
      _free(i);
    },

    /**
     * Mark a loop as fading out. It still holds its node, so it still counts
     * against both budgets — but it is now the FIRST thing stealing takes,
     * whatever its priority, because it is already leaving.
     * @param {number} voice
     */
    fade(voice) {
      if (disposed || !usable(voice)) return;
      const i = Math.floor(voice);
      if (i < 0 || i >= pool.length) return;
      if (pool[i].state === VOICE.LIVE) pool[i].state = VOICE.FADING;
    },

    /**
     * A loop asked to stay alive. `Sfx.loop(slug, key)` is idempotent and may be
     * called every frame, so this is the cheap path — it records that the voice
     * is still wanted and does nothing else.
     *
     * ⚠ It does NOT reset `startedAt`, and therefore does not make a loop
     * unstealable by being touched. Age in the steal rank is age since the
     * voice STARTED: a loop that has been running for five minutes has had its
     * turn, and one that could refresh its own seniority every frame would be
     * immortal.
     *
     * @param {number} voice @param {number} [now]
     */
    touch(voice, now = undefined) {
      if (disposed || !usable(voice)) return;
      const i = Math.floor(voice);
      if (i < 0 || i >= pool.length) return;
      const t = usable(now) ? now : lastNow;
      lastNow = t;
      if (pool[i].state !== VOICE.FREE) pool[i].touchedAt = t;
    },

    /**
     * Reap expired one-shots. ⚠ A LOOP IS NEVER REAPED — it ends when somebody
     * says so (`stopLoop` -> `fade` -> `release`) and not when a clock runs out,
     * which is the whole difference between the two kinds.
     * @param {number} [now] Seconds, absolute.
     */
    update(now = undefined) {
      if (disposed) return;
      const t = usable(now) ? now : lastNow;
      lastNow = t;
      for (let i = 0; i < pool.length; i++) {
        const v = pool[i];
        if (v.state === VOICE.FREE || v.loop) continue;
        if (t >= v.endsAt) _free(i);
      }
    },

    // ── per-frame re-evaluation, for loops ───────────────────────────────────

    /**
     * What a live positional voice should sound like RIGHT NOW. Loops move, the
     * listener moves and the camera turns, so this is re-asked every frame for
     * every loop — and the backend ramps to it rather than stepping, because a
     * step on a running source is a click and a click is the one artifact
     * distance attenuation cannot hide.
     *
     * A 2D slug (no usable position) keeps its mix gain and centre pan, so
     * calling this on the rain bed is safe and is a no-op in effect.
     *
     * @param {number} x @param {number} y @param {number} z @param {string} slug
     * @returns {{gain: number, pan: number}} An unknown slug is silent, not 1.
     */
    gainPanFor(x, y, z, slug) {
      const def = mix?.[slug];
      if (!def) return { gain: 0, pan: 0 };
      const base = num(def.gain, 1);
      if (!usable(x) || !usable(z)) return { gain: base, pan: 0 };
      const dx = x - lx;
      const dy = num(y, ly) - ly;
      const dz = z - lz;
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
      return {
        gain: base * attenuate(d, num(def.refDist, 1), num(def.maxDist, Infinity), cullFade),
        pan: panFor(dx, dz, lyaw, panWidth),
      };
    },

    // ── variant selection ────────────────────────────────────────────────────

    /**
     * Which clip of a variant set plays.
     *
     * Three paths, in this order:
     *   1. `def.variant` PINS it — zero dice. `explosion` uses this: the second
     *      `car_crash` is a 2.57 s tumble and pitching it down yields ten
     *      seconds of scraping.
     *   2. `pick: 'sticky'` — `hashSeed(slug + ':' + key)`, ZERO DICE. A car
     *      keeps its engine across a save, a reload and a level rebuild. ⚠ This
     *      is why `sfxAssets.js` calls variant ORDER a contract: reordering the
     *      list repaints every car in the game.
     *   3. `pick: 'random'` (the default) — ONE die, and only when the slug
     *      really has more than one clip.
     *
     * ⚠ **WITH NO `assets` INJECTED THIS ALWAYS RETURNS 0**, deterministically
     * and without a die: one variant is all a mixer that was never told what
     * exists can prove is there. That is a real configuration (a suite, a dev
     * scene) and it is documented rather than guarded.
     *
     * @param {string} slug @param {string} [key]
     * @returns {number} An index into `assets[src].files`.
     */
    variantFor(slug, key = undefined) {
      const def = mix?.[slug];
      if (!def) return 0;
      const n = _variantCount(def, slug);

      if (usable(def.variant)) return clamp(Math.floor(def.variant), 0, n - 1);
      if (n <= 1) return 0;

      if (def.pick === 'sticky') return Math.floor(hash01(`${slug}:${key ?? ''}`) * n) % n;
      return Math.floor(rng() * n) % n;
    },

    // ── readouts ─────────────────────────────────────────────────────────────

    /** @param {number} voice @returns {number} One of {@link VOICE}. */
    stateOf(voice) {
      if (!usable(voice)) return VOICE.FREE;
      const i = Math.floor(voice);
      return i >= 0 && i < pool.length ? pool[i].state : VOICE.FREE;
    },

    /** @returns {number} The pool size — `tune.maxVoices`, resolved. */
    get size() {
      return pool.length;
    },

    /**
     * @returns {{live: number, loops: number, stolen: number, culled: number,
     *   dropped: number, peak: number}} `culled` is the distance reject alone —
     *   it is expected to dwarf everything else and is not a problem. `dropped`
     *   is every other refusal; a `dropped` that climbs with the pool half empty
     *   means a `minGap` or a per-slug cap is too tight.
     */
    stats() {
      const { live, loops } = _count();
      return { live, loops, stolen, culled, dropped, peak };
    },

    /**
     * Idempotent. Frees every slot and refuses everything afterwards, so a
     * backend torn down mid-frame cannot be handed a voice on its way out. The
     * `Wallet#dispose` rule.
     */
    dispose() {
      if (disposed) return;
      disposed = true;
      for (let i = 0; i < pool.length; i++) _free(i);
      lastStart.clear();
    },
  };

  return api;
}
